import * as DocumentPicker from 'expo-document-picker';
import { File } from 'expo-file-system';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronLeft,
  Download,
  FileText,
  Upload,
} from 'lucide-react-native';
import { useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNotice } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import {
  INVENTORY_SAMPLE_CSV,
  downloadCsvSample,
  inventorySampleFilename,
} from '@/lib/csv-samples';
import {
  importInventoryCsv,
  parseInventoryCsv,
  type CsvPreviewRow,
} from '@/lib/inventory-csv';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';

/**
 * Bulk inventory CSV importer. Pick a file → see the preview of every
 * parsed row → confirm to insert. Anything missing a name is flagged
 * and skipped. Designed for the spreadsheet someone already keeps —
 * the column mapper accepts most common header variants.
 */
export default function InventoryImport() {
  const router = useRouter();
  const db = useSQLiteContext();
  const notice = useNotice();
  const [rows, setRows] = useState<CsvPreviewRow[]>([]);
  const [filename, setFilename] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);

  const pickFile = async () => {
    setParsing(true);
    try {
      const res = await DocumentPicker.getDocumentAsync({
        type: ['text/csv', 'text/plain', 'application/csv', '*/*'],
        copyToCacheDirectory: true,
      });
      if (res.canceled || !res.assets[0]) return;
      const asset = res.assets[0];
      setFilename(asset.name ?? 'inventory.csv');
      let text: string;
      if (Platform.OS === 'web') {
        const r = await fetch(asset.uri);
        text = await r.text();
      } else {
        const f = new File(asset.uri);
        text = await f.text();
      }
      const parsed = parseInventoryCsv(text);
      setRows(parsed);
      if (parsed.length === 0) {
        void notice({
          title: 'No rows found',
          message: 'The file looked empty. Make sure the first row is a header (e.g. name, room, brand).',
          tone: 'warning',
        });
      }
    } catch (err) {
      void notice({ title: "Couldn't read file", message: String(err), tone: 'error' });
    } finally {
      setParsing(false);
    }
  };

  const doImport = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      const result = await importInventoryCsv(db, prop.id, rows);
      const msg = result.skipped > 0
        ? `Imported ${result.inserted}. Skipped ${result.skipped}${result.errors.length ? `:\n${result.errors.slice(0, 3).map((e) => `Row ${e.row}: ${e.message}`).join('\n')}` : '.'}`
        : `Imported ${result.inserted} item${result.inserted === 1 ? '' : 's'}.`;
      await notice({ title: 'Import done', message: msg, tone: result.skipped > 0 ? 'warning' : 'info' });
      router.replace('/inventory' as never);
    } catch (err) {
      void notice({ title: 'Import failed', message: String(err), tone: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const validCount = rows.filter((r) => r.ok).length;
  const invalidCount = rows.length - validCount;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/inventory')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Import CSV</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Bulk-load your inventory</Text>
        <Text style={styles.sub}>
          Drop in a CSV from Excel, Numbers, Google Sheets, or any home-inventory
          template. We map the columns you have and skip the rest.
        </Text>

        <View style={styles.card}>
          <Text style={styles.cardLabel}>EXPECTED COLUMNS</Text>
          <Text style={styles.cardBody}>
            <Text style={styles.req}>name</Text> is required. Optional:
            room, category, brand, model, serial, vendor, purchase date,
            purchase price, current value, condition, insured (yes/no),
            notes. Headers are matched loosely so "Model #" and "model
            number" both work.
          </Text>
          <Pressable
            onPress={() => downloadCsvSample(INVENTORY_SAMPLE_CSV, inventorySampleFilename())}
            style={({ pressed }) => [styles.sampleBtn, pressed && { opacity: 0.85 }]}>
            <Download size={14} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.sampleBtnText}>Download sample CSV</Text>
          </Pressable>
        </View>

        <Pressable
          onPress={pickFile}
          disabled={parsing}
          style={({ pressed }) => [
            styles.pickBtn,
            pressed && { transform: [{ scale: 0.98 }] },
            parsing && { opacity: 0.6 },
          ]}>
          <Upload size={18} color={Palette.forest} strokeWidth={2} />
          <Text style={styles.pickText}>
            {parsing ? 'Reading file…' : filename ? `Replace: ${filename}` : 'Pick a CSV file'}
          </Text>
        </Pressable>

        {rows.length > 0 ? (
          <>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryText}>
                <Text style={styles.summaryBold}>{validCount}</Text> valid
                {invalidCount > 0 ? (
                  <>
                    {' · '}
                    <Text style={[styles.summaryBold, { color: Palette.clay }]}>
                      {invalidCount} to skip
                    </Text>
                  </>
                ) : null}
              </Text>
            </View>

            <View style={styles.previewList}>
              {rows.slice(0, 40).map((r) => (
                <View key={r.index} style={[styles.previewRow, !r.ok && styles.previewRowBad]}>
                  <Text style={styles.previewIdx}>#{r.index}</Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.previewName} numberOfLines={1}>
                      {r.ok ? r.parsed?.name : (r.reason ?? 'Invalid')}
                    </Text>
                    {r.ok && r.parsed ? (
                      <Text style={styles.previewMeta} numberOfLines={1}>
                        {[r.parsed.brand, r.parsed.room, r.parsed.category].filter(Boolean).join(' · ')}
                      </Text>
                    ) : null}
                  </View>
                  {r.ok ? (
                    <Check size={14} color={Palette.moss} strokeWidth={2.2} />
                  ) : (
                    <Text style={styles.badTag}>SKIP</Text>
                  )}
                </View>
              ))}
              {rows.length > 40 ? (
                <Text style={styles.previewMore}>
                  …and {rows.length - 40} more
                </Text>
              ) : null}
            </View>

            <Pressable
              onPress={doImport}
              disabled={importing || validCount === 0}
              style={({ pressed }) => [
                styles.importBtn,
                (importing || validCount === 0) && { opacity: 0.5 },
                pressed && { transform: [{ scale: 0.98 }] },
              ]}>
              <FileText size={18} color={Palette.cream} strokeWidth={2} />
              <Text style={styles.importText}>
                {importing
                  ? 'Importing…'
                  : `Import ${validCount} item${validCount === 1 ? '' : 's'}`}
              </Text>
            </Pressable>
          </>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  cardLabel: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint, letterSpacing: 0.6,
  },
  cardBody: {
    marginTop: 6, fontFamily: Fonts.sansMedium, fontSize: 13.5,
    color: Palette.inkSoft, lineHeight: 18,
  },
  req: { fontFamily: Fonts.sansBold, color: Palette.forest },
  sampleBtn: {
    marginTop: 10,
    flexDirection: 'row', alignItems: 'center', gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 10, paddingVertical: 7, borderRadius: 999,
    backgroundColor: '#EAF0EA',
    borderWidth: 1, borderColor: Palette.line,
  },
  sampleBtnText: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest },
  pickBtn: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: '#EAF0EA',
    borderWidth: 1.5, borderStyle: 'dashed', borderColor: Palette.forest,
    paddingVertical: 16, borderRadius: 14,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.forest },
  summaryRow: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
  },
  summaryText: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
  },
  summaryBold: { fontFamily: Fonts.sansBold, color: Palette.forest },
  previewList: {
    marginTop: 8, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.lg, paddingHorizontal: 12,
  },
  previewRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  previewRowBad: { opacity: 0.7 },
  previewIdx: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint, width: 28,
  },
  previewName: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  previewMeta: {
    marginTop: 1, fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  badTag: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.clay,
    backgroundColor: 'rgba(176,95,60,0.15)',
    paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, letterSpacing: 0.5,
  },
  previewMore: {
    paddingVertical: 10, textAlign: 'center',
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
  },
  importBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
  },
  importText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
});
